import { Tooltip } from '@mui/material';
import classnames from 'classnames';
import { CSSProperties, JSX, MouseEventHandler, Ref, useEffect } from 'react';
import { Check } from 'react-feather';
import { Link } from 'react-router';

import CircularLoader from 'components/CircularLoader';

import styles from './Button.module.scss';

export enum ButtonType {
  Primary = 'primary',
  Secondary = 'secondary',
  Minimal = 'minimal',
  Link = 'link',
}

export enum ButtonSpacing {
  Normal = 'normal',
  Compressed = 'compressed',
}

export enum ButtonColor {
  Normal = 'normal',
  Positive = 'positive',
  Negative = 'negative',
}

interface ButtonProps {
  id?: string;
  className?: string;
  style?: CSSProperties;
  children: JSX.Element[] | JSX.Element;
  onClick?: MouseEventHandler;
  to?: string;
  color?: ButtonColor;
  submit?: boolean;
  disabled?: boolean;
  type?: ButtonType;
  spacing?: ButtonSpacing;
  loading?: boolean;
  tooltip?: string | JSX.Element | JSX.Element[] | null;
  showSuccess?: boolean;
  onSuccessComplete?: () => void;
  successText?: string | null;
  ariaLabel?: string;
  ref?: Ref<HTMLButtonElement & HTMLAnchorElement>;
}

export default function Button({
  id,
  className,
  style,
  children,
  onClick,
  to,
  submit,
  tooltip,
  disabled = false,
  type = ButtonType.Primary,
  spacing = ButtonSpacing.Normal,
  color = ButtonColor.Normal,
  loading = false,
  showSuccess = false,
  onSuccessComplete,
  successText,
  ariaLabel,
  ref,
}: ButtonProps): JSX.Element {
  useEffect(() => {
    if (showSuccess && onSuccessComplete) {
      const timer = setTimeout(() => {
        onSuccessComplete();
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [showSuccess, onSuccessComplete]);

  const spacingStyles = {
    [styles.NormalSpacing]:
      type !== ButtonType.Link && spacing === ButtonSpacing.Normal,
    [styles.CompressedSpacing]:
      type !== ButtonType.Link && spacing === ButtonSpacing.Compressed,
  };

  const typeStyles = {
    [styles.PrimaryType]: type === ButtonType.Primary,
    [styles.SecondaryType]: type === ButtonType.Secondary,
    [styles.MinimalType]: type === ButtonType.Minimal,
    [styles.LinkType]: type === ButtonType.Link,
  };

  const colorStyles = {
    [styles.NormalColor]: color === ButtonColor.Normal && !disabled,
    [styles.PositiveColor]: color === ButtonColor.Positive && !disabled,
    [styles.NegativeColor]: color === ButtonColor.Negative && !disabled,
    [styles.Disabled]: disabled,
  };

  const buttonClasses: string = classnames(
    'syl-button',
    styles.Button,
    className,
    spacingStyles,
    typeStyles,
    colorStyles
  );

  const classes: string = tooltip
    ? buttonClasses
    : classnames(buttonClasses, className);

  const buttonContents = loading ? (
    <CircularLoader className={styles.Loader} />
  ) : showSuccess ? (
    <div className={styles.SuccessContainer}>
      <Check size={17} className={styles.SuccessCheck} />
      {successText && <span className={styles.SuccessText}>{successText}</span>}
    </div>
  ) : (
    children
  );

  const button =
    to && !disabled ? (
      <Link
        ref={ref}
        className={classnames(styles.LinkButton, className)}
        to={to}
        aria-label={ariaLabel}
        onClick={onClick}
      >
        <div className={classes}>{buttonContents}</div>
      </Link>
    ) : (
      <button
        id={id}
        ref={ref}
        className={classes}
        type={submit ? 'submit' : 'button'}
        onClick={onClick}
        disabled={disabled}
        style={style}
        aria-label={ariaLabel}
      >
        {buttonContents}
      </button>
    );

  return tooltip ? (
    <Tooltip title={tooltip}>
      <span className={className}>{button}</span>
    </Tooltip>
  ) : (
    button
  );
}
